import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";
import * as moment from "moment";
import DeleteModal from "../../Components/Common/DeleteModal";
import AccountCreationModal from "./AccountCreationModal";
import { Card, CardBody, Col, Container, CardHeader, Row } from "reactstrap";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import TableContainer from "../../Components/Common/TableContainer";
import Loader from "../../Components/Common/Loader";
import { useProfile } from "../../Components/Hooks/UserHooks";

import {
  getAccounts as onGetAccounts,
  deleteAccount as onDeleteAccount,
} from "../../slices/accounts/thunk";

const selectAccountState = createSelector(
  (state) => state.Accounts,
  (accounts) => ({
    accounts: accounts.accounts || [],
    account: accounts.account || null,
    isAccountSuccess: accounts.isAccountSuccess || false,
    isAccountCreated: accounts.isAccountCreated || false,
    isAccountFetched: accounts.isAccountFetched || false,
    isLoading: accounts.isLoading || false,
    error: accounts.error,
  })
);

const createCommonColumn = (header, accessorKey, enableFilter = false, customCell = null) => ({
  header,
  accessorKey,
  enableColumnFilter: enableFilter,
  headerStyle: { textAlign: "center" },
  cell: customCell || ((cell) => <div className="text-center">{cell.getValue()}</div>),
});

const createStatusColumn = () => ({
  header: "Status",
  accessorKey: "id",
  enableColumnFilter: false,
  headerStyle: { textAlign: "center" },
  cell: (cell) => {
    const row = cell.row.original;
    const hasToken = row.token ? true : false;
    const hasPhase = row.phase ? true : false;

    return (
      <div className="d-flex align-items-center justify-content-center gap-2">
        {hasToken && hasPhase && (
          <span className="badge bg-success d-flex align-items-center p-2 rounded">
            <i className="ri-checkbox-circle-line me-1 fs-5"></i>
            Active
          </span>
        )}
        {!hasToken && (
          <span className="badge bg-warning d-flex align-items-center p-2 rounded">
            <i className="ri-key-line me-1 fs-5"></i>
            No Token
          </span>
        )}
        {!hasPhase && (
          <span className="badge bg-info d-flex align-items-center p-2 rounded">
            <i className="ri-phase-line me-1 fs-5"></i>
            No Phase
          </span>
        )}
      </div>
    );
  },
});

const createActionColumn = (hasDeleteRight, onClickDelete) => ({
  id: "actions",
  enableColumnFilter: false,
  header: "",
  size: 60,
  headerStyle: { textAlign: "center" },
  cell: (cellProps) => {
    return (
      <div className="text-center">
        <ul className="list-inline d-flex justify-content-center align-items-center gap-2 mb-0">
          {hasDeleteRight && (
            <li className="list-inline-item">
              <button
                className="btn btn-sm btn-icon btn-ghost-danger"
                onClick={(e) => {
                  e.stopPropagation();
                  onClickDelete(cellProps.row.original);
                }}
                title="Delete Account"
              >
                <i className="ri-delete-bin-5-fill fs-16"></i>
              </button>
            </li>
          )}
        </ul>
      </div>
    );
  },
});

const AccountManagement = () => {
  const [deleteModal, setDeleteModal] = useState(false);
  const [createModal, setCreateModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userProfile, isAdmin, isEditor } = useProfile();

  const {
    accounts,
    isAccountSuccess,
    isLoading,
    error,
  } = useSelector(selectAccountState);

  useEffect(() => {
    const handleResize = () => {
      setPageSize(window.innerWidth <= 480 ? 5 : 10);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    dispatch(onGetAccounts());
  }, [dispatch]);

  const formatDateTime = (date) => {
    if (!date) return "No data";
    return moment(new Date(date)).format("DD MMM Y, HH:mm");
  };

  const maskToken = (token) => {
    if (!token) return "No token";
    if (token.length <= 8) return token;
    return `${token.substring(0, 4)}...${token.substring(token.length - 4)}`;
  };

  const handleCreateAccountClick = () => {
    setCreateModal(true);
  };

  const toggleCreateModal = () => {
    setCreateModal(!createModal);
  };

  const onClickDelete = useCallback((account) => {
    setSelectedAccount(account);
    setDeleteModal(true);
  }, []);

  const handleRowClick = useCallback((row) => {
    navigate(`/admin/panel/accounts/${row.original.id}`);
  }, [navigate]);

  const handleDeleteAccount = useCallback(async () => {
    if (selectedAccount) {
      setIsDeleting(true);
      setDeleteError(null);
      try {
        await dispatch(onDeleteAccount(selectedAccount.id)).unwrap();
        setDeleteModal(false);
        setSelectedAccount(null);
        dispatch(onGetAccounts());
      } catch (err) {
        const errorMessage =
          err.response?.data?.message ||
          err.message ||
          "Failed to delete account";
        setDeleteError(errorMessage);
      } finally {
        setIsDeleting(false);
      }
    }
  }, [dispatch, selectedAccount]);

  const columns = useMemo(() => [
    createCommonColumn("CIF", "cif", true, (cell) => (
      <div className="text-center">
        <span className="fw-medium text-primary">{cell.getValue()}</span>
      </div>
    )),
    createCommonColumn("Email", "email", true, (cell) => (
      <div className="text-center">
        <span className="text-muted">{cell.getValue()}</span>
      </div>
    )),
    createCommonColumn("Token", "token", false, (cell) => (
      <div className="text-center">
        <code className="text-muted small">{maskToken(cell.getValue())}</code>
      </div>
    )),
    createCommonColumn("Phase", "phase_name", true, (cell) => (
      <div className="text-center">
        <span className="badge bg-soft-info text-info">
          {cell.getValue() || "No phase"}
        </span>
      </div>
    )),
    createCommonColumn("Created", "created_at", false, (cell) => (
      <div className="text-center text-muted small">
        {formatDateTime(cell.getValue())}
      </div>
    )),
    createCommonColumn("Updated", "updated_at", false, (cell) => (
      <div className="text-center text-muted small">
        {formatDateTime(cell.getValue())}
      </div>
    )),
    createStatusColumn(),
    createActionColumn(
      isAdmin || isEditor,
      onClickDelete
    ),
  ], [isAdmin, isEditor, onClickDelete]);

  return (
    <div className="page-content">
      <AccountCreationModal
        isOpen={createModal}
        toggle={toggleCreateModal}
      />

      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteAccount}
        onCloseClick={() => {
          setDeleteModal(false);
          setSelectedAccount(null);
          setDeleteError(null);
        }}
        isDeleting={isDeleting}
        error={deleteError}
      />

      <Container fluid>
        <Row>
          <Col lg={12}>
            <Card id="accountList" className="shadow-sm">
              <CardHeader className="border-0 bg-light">
                <div className="d-flex align-items-center justify-content-between">
                  <h5 className="card-title mb-0">SmartBill Accounts</h5>
                  {(isAdmin || isEditor) && (
                    <button
                      type="button"
                      className="btn btn-success"
                      onClick={handleCreateAccountClick}
                    >
                      <i className="ri-add-line align-bottom me-1"></i> Add SmartBill Account
                    </button>
                  )}
                </div>
              </CardHeader>

              <CardBody>
                <div className="alert alert-info d-flex align-items-start" role="alert">
                  <i className="ri-information-line me-2 fs-4"></i>
                  <div>
                    These credentials are used to send invoices to SmartBill. Each account can be linked to a specific Phase; when invoices are generated for units in that Phase, the linked SmartBill account is used (CIF, email, API token, and default series).
                  </div>
                </div>
                {isLoading ? (
                  <Loader />
                ) : isAccountSuccess ? (
                  accounts && accounts.length > 0 ? (
                    <TableContainer
                      columns={columns}
                      data={accounts}
                      customPageSize={pageSize}
                      isGlobalFilter={true}
                      divClass="table-responsive table-card mb-1"
                      tableClass="align-middle table-nowrap table-hover"
                      theadClass="table-light text-muted text-uppercase text-center"
                      SearchPlaceholder="Search SmartBill accounts..."
                      handleRowClick={handleRowClick}
                      customTableRowProps={(row) => ({
                        className: "cursor-pointer",
                        style: { cursor: "pointer" }
                      })}
                      initialState={{
                        columnVisibility: {},
                        sorting: [{ id: "cif", desc: false }],
                      }}
                    />
                  ) : (
                    <div className="text-center py-5">
                      <div className="avatar-md mx-auto mb-4">
                        <div className="avatar-title rounded-circle bg-light text-primary fs-24">
                          <i className="ri-bill-line"></i>
                        </div>
                      </div>
                      <h5>No SmartBill Accounts Found</h5>
                      <p className="text-muted">
                        Add at least one SmartBill account (CIF, email, API token and series) to enable automatic invoicing.
                      </p>
                      {(isAdmin || isEditor) && (
                        <button
                          className="btn btn-primary"
                          onClick={handleCreateAccountClick}
                        >
                          <i className="ri-add-line me-1"></i>
                          Add SmartBill Account
                        </button>
                      )}
                    </div>
                  )
                ) : (
                  <div className="text-center mt-5">
                    <p className="text-muted">
                      {error || "Error loading accounts. Please try again."}
                    </p>
                    <button
                      className="btn btn-outline-primary"
                      onClick={() => dispatch(onGetAccounts())}
                    >
                      <i className="ri-refresh-line me-1"></i>
                      Retry
                    </button>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default AccountManagement;
